import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { RegisterSessionSchema, RegisterSessionCreateInputSchema, RegisterSessionUpdateInputSchema, type RegisterSession, type RegisterSessionCreateInput, type RegisterSessionUpdateInput } from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import {
  type CountOptions,
  type ListOptions,
  runAverage,
  runCount,
  runList,
  runSum,
} from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `registerSession` (période de caisse ouverte puis
 * clôturée sur un point d'encaissement, org-wide en v0).
 *
 * Réplique le PATRON `asset` : l'entrée est VALIDÉE par Zod
 * (`RegisterSessionCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit
 * jamais un document pour découvrir ensuite qu'il est invalide. Les montants de
 * caisse (fond, attendu, compté, écart) sont sensibles et ne vivent PAS dans ce
 * document org-wide en v0 (cf. data-model).
 */

export type { RegisterSessionCreateInput };

/** Nom de la collection Firestore de la famille registerSession. */
export const REGISTER_SESSIONS_COLLECTION = 'registerSessions';

/**
 * Repository typé pour la famille `registerSession`.
 * Toute lecture est validée par `RegisterSessionSchema`, toute écriture par
 * `RegisterSessionCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface RegisterSessionRepository {
  create(input: RegisterSessionCreateInput): Promise<RegisterSession>;
  update(id: string, patch: RegisterSessionUpdateInput): Promise<RegisterSession>;
  list(options?: ListOptions): Promise<RegisterSession[]>;
  count(options?: CountOptions): Promise<number>;
  /** Somme serveur d'un champ, memes options que `count`. Cf. list-query.ts. */
  sum(field: string, options?: CountOptions): Promise<number>;
  /** Moyenne serveur d'un champ, `null` si aucun document. Cf. list-query.ts. */
  average(field: string, options?: CountOptions): Promise<number | null>;
  get(id: string): Promise<RegisterSession | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<RegisterSession>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<RegisterSession>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<RegisterSession>;
}

const registerSessionConverter = createConverter<RegisterSession>((raw: DocumentData) => RegisterSessionSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createRegisterSessionRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): RegisterSessionRepository {
  const col = collection(db, REGISTER_SESSIONS_COLLECTION).withConverter(registerSessionConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<RegisterSession>(db, col, REGISTER_SESSIONS_COLLECTION, getActorId);

  return {
    async create(input: RegisterSessionCreateInput): Promise<RegisterSession> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = RegisterSessionCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.openedByWorkerId !== undefined) optional['openedByWorkerId'] = fields.openedByWorkerId;
      if (fields.openedAt !== undefined) optional['openedAt'] = fields.openedAt;
      if (fields.closedAt !== undefined) optional['closedAt'] = fields.closedAt;
      if (fields.status !== undefined) optional['status'] = fields.status;
      if (fields.paymentIds !== undefined) optional['paymentIds'] = fields.paymentIds;
      if (fields.incidentId !== undefined) optional['incidentId'] = fields.incidentId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;
      if (fields.couleur != null) optional['couleur'] = fields.couleur;
      if (fields.photo != null) optional['photo'] = fields.photo;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        placeId: fields.placeId,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as RegisterSession);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return RegisterSessionSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: RegisterSessionUpdateInput): Promise<RegisterSession> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = RegisterSessionUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, REGISTER_SESSIONS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`registerSession ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<RegisterSession[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async sum(field: string, options?: CountOptions): Promise<number> {
      // Sommer sans charger, meme cadrage que `count` (cf. runSum,
      // list-query.ts). Rend 0 quand aucun document ne passe le filtre.
      return runSum(col, orgId, field, options);
    },

    async average(field: string, options?: CountOptions): Promise<number | null> {
      // `null` quand aucun document ne passe le filtre : c'est ce que rend
      // Firestore, et un zero mentirait (cf. runAverage, list-query.ts).
      return runAverage(col, orgId, field, options);
    },

    async get(id: string): Promise<RegisterSession | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
